/* eslint-disable react/prop-types */
import React, { ReactElement, useEffect, useRef } from "react";

import { Drawer } from "antd";

import { PopInProps } from "./popIn.model";
import { Button } from "@companion-core/shared/app/Components/Button";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import "@companion-core/web/src/Assets/Styles/scss/popIn.scss";

const getPopInButtons = (container: HTMLElement | null) =>
  Array.from(
    container?.querySelectorAll<HTMLElement>(
      '[data-testid="popin_secondary_button"], [data-testid="popin_primary_button"]'
    ) ?? []
  ).filter(
    (element) =>
      !element.hasAttribute("disabled") && element.getAttribute("aria-disabled") !== "true"
  );

export const PopIn = ({
  testID,
  isOpen,
  icon,
  title,
  content,
  primaryButton,
  secondaryButton,
  onClose,
  freeze,
  dialogTitleId,
}: PopInProps) => {
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const isAccessibleDialog = !!dialogTitleId && !isMobileSize;

  useEffect(() => {
    if (!isAccessibleDialog) {
      return;
    }

    if (!isOpen) {
      triggerRef.current?.focus();
      triggerRef.current = null;
      return;
    }

    triggerRef.current ??= document.activeElement as HTMLElement | null;

    const title = titleRef.current;
    const removeTitleFromTabOrder = () => title?.removeAttribute("tabindex");

    window.requestAnimationFrame(() => title?.focus());
    title?.addEventListener("blur", removeTitleFromTabOrder);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !freeze) {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (event.key !== "Tab" || !secondaryButton || freeze) {
        return;
      }

      const buttons = getPopInButtons(containerRef.current);
      if (buttons.length < 2) {
        return;
      }

      const [firstButton, lastButton] = [buttons[0], buttons[buttons.length - 1]];
      const activeElement = document.activeElement;

      if (event.shiftKey && activeElement === firstButton) {
        event.preventDefault();
        lastButton.focus();
      } else if (!event.shiftKey && activeElement === lastButton) {
        event.preventDefault();
        firstButton.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      title?.removeEventListener("blur", removeTitleFromTabOrder);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [freeze, isAccessibleDialog, isOpen, onClose, secondaryButton]);

  const Content = () => (
    <>
      <div className="popin__texts">
        {title && (
          <h2
            ref={dialogTitleId ? titleRef : undefined}
            {...(dialogTitleId ? { id: dialogTitleId, tabIndex: -1 } : {})}
            className="body-text-5">
            {title}
          </h2>
        )}
        {content &&
          (typeof content === "string" ? (
            <p className="body-text-6">{content}</p>
          ) : (
            (content as ReactElement)
          ))}
      </div>
      <div className="popin__buttons">
        {secondaryButton && (
          <Button
            testID="popin_secondary_button"
            type="secondary"
            size={secondaryButton.size}
            classNames={["popin__buttons__button"]}
            label={secondaryButton.text}
            accessibilityLabel={secondaryButton.accessibilityLabel}
            onClick={secondaryButton.onClick}
          />
        )}
        {primaryButton && (
          <Button
            testID="popin_primary_button"
            classNames={["popin__buttons__button"]}
            label={primaryButton.text}
            size={primaryButton.size}
            accessibilityLabel={primaryButton.accessibilityLabel}
            onClick={primaryButton.onClick}
            disabled={freeze}
          />
        )}
      </div>
    </>
  );

  return isMobileSize ? (
    <Drawer
      open={isOpen}
      onClose={freeze ? undefined : onClose}
      closeIcon={null}
      placement="bottom"
      className="popin__drawer"
      styles={{ wrapper: { height: "min-content" } }}>
      {icon && (
        <FontIcon
          className="popin__icon"
          name={icon}
          size={24}
          {...(dialogTitleId ? { "aria-hidden": true } : {})}
        />
      )}
      <Content />
    </Drawer>
  ) : (
    isOpen && (
      <>
        <div className="popin__overlay" onClick={freeze ? undefined : onClose} />
        <div
          ref={containerRef}
          className="popin__container"
          data-testid={testID}
          {...(isAccessibleDialog
            ? {
                role: "dialog",
                "aria-modal": true,
                "aria-labelledby": dialogTitleId,
              }
            : {})}>
          {icon && (
            <FontIcon
              className="popin__icon"
              name={icon}
              size={24}
              {...(dialogTitleId ? { "aria-hidden": true } : {})}
            />
          )}
          <Content />
        </div>
      </>
    )
  );
};
